import { useMemo } from 'react';
import {
  ChakraFlex,
  ChakraVStack,
  FAIconName,
  Icon,
  Text,
  Tag,
  TagColor,
} from '@equip.health/ui';
import AppointmentCard from '~/components/common/calendar/AppointmentCard';
import {
  APPOINTMENT_LIST_TEST_ID,
  APPOINTMENTS_SECTION_LABEL,
  PROPOSED_APPOINTMENTS_SECTION_LABEL,
  PROPOSED_BADGE_TEXT,
} from '../constants';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  formatShortDate,
  formatAppointmentShortTime,
  formatRecurrenceLabel,
} from '../utils';

export interface ProposedAppointment {
  startTime: string;
  endTime: string;
  appointmentType: string;
  provider: GetProviderByIdResponse;
  recurrence?: string;
}

export interface AppointmentListProps {
  existingAppointments: AppointmentDetailResponse[];
  patientTimezone?: string;
  proposedAppointment: ProposedAppointment | null;
  proposedFollowUps?: FollowUpOpening[];
  recurringAppointmentType?: string | null;
}

const AppointmentList = ({
  existingAppointments,
  patientTimezone,
  proposedAppointment,
  proposedFollowUps = [],
  recurringAppointmentType,
}: AppointmentListProps) => {
  const formatDate = (isoString: string) =>
    formatShortDate(isoString, patientTimezone);
  const formatStartEndTime = (startIso: string, endIso: string) =>
    formatAppointmentShortTime(startIso, endIso, patientTimezone);

  // Sort appointments chronologically (earliest first)
  const sortedAppointments = useMemo(
    () =>
      [...existingAppointments].sort(
        (a, b) =>
          new Date(a.appointmentStartDateTime).getTime() -
          new Date(b.appointmentStartDateTime).getTime(),
      ),
    [existingAppointments],
  );

  const sortedFollowUps = useMemo(
    () =>
      [...proposedFollowUps].sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ),
    [proposedFollowUps],
  );

  const hasFollowUps = sortedFollowUps.length > 0;

  const hasAnyAppointments =
    sortedAppointments.length > 0 ||
    proposedAppointment !== null ||
    hasFollowUps;

  const sectionLabel = hasFollowUps
    ? PROPOSED_APPOINTMENTS_SECTION_LABEL
    : APPOINTMENTS_SECTION_LABEL;

  if (!hasAnyAppointments) {
    return (
      <ChakraFlex
        data-testid={APPOINTMENT_LIST_TEST_ID}
        flexDirection="column"
        gap="18px"
      >
        <Text variant="bodyMedium">{APPOINTMENTS_SECTION_LABEL}</Text>
        <ChakraFlex
          alignItems="center"
          color="neutral.secondary"
          gap="8px"
          padding="18px"
        >
          <Icon name={FAIconName.calendarRegular} />
          <Text variant="body">No appointments scheduled</Text>
        </ChakraFlex>
      </ChakraFlex>
    );
  }

  return (
    <ChakraFlex
      data-testid={APPOINTMENT_LIST_TEST_ID}
      flexDirection="column"
      gap="18px"
    >
      <Text variant="bodyMedium">{sectionLabel}</Text>
      <ChakraVStack alignItems="stretch" spacing="12px">
        {sortedAppointments.map((appointment) => (
          <AppointmentCard
            avatarProps={{
              name: getPreferredFullName(
                null,
                appointment.host.firstName,
                appointment.host.lastName,
              ),
              src: appointment.host.avatar?.url,
              userType: 'provider',
            }}
            endTimeISO={appointment.appointmentEndDateTime}
            formatDate={formatDate}
            formatStartEndTime={formatStartEndTime}
            id={`existing-appointment-${appointment.appointmentExternalId}`}
            key={appointment.appointmentExternalId}
            recurrenceLabel={formatRecurrenceLabel(
              appointment.recurrence,
              appointment.appointmentStartDateTime,
            )}
            startTimeISO={appointment.appointmentStartDateTime}
            title={appointment.appointmentTypeLabel}
          />
        ))}

        {proposedAppointment && (
          <AppointmentCard
            avatarProps={{
              name: getPreferredFullName(
                null,
                proposedAppointment.provider.firstName,
                proposedAppointment.provider.lastName,
              ),
              src: proposedAppointment.provider.avatar?.url,
              userType: 'provider',
            }}
            badge={
              !hasFollowUps ? (
                <Tag
                  id={`proposed-appointment-badge-${proposedAppointment.provider.userId}`}
                  color={TagColor.pink}
                  value={PROPOSED_BADGE_TEXT}
                />
              ) : undefined
            }
            endTimeISO={proposedAppointment.endTime}
            formatDate={formatDate}
            formatStartEndTime={formatStartEndTime}
            id={`proposed-appointment-${proposedAppointment.provider.userId}`}
            recurrenceLabel={formatRecurrenceLabel(
              proposedAppointment.recurrence,
              proposedAppointment.startTime,
            )}
            startTimeISO={proposedAppointment.startTime}
            title={proposedAppointment.appointmentType}
          />
        )}

        {sortedFollowUps.map((opening) => (
          <AppointmentCard
            endTimeISO={opening.endTime}
            formatDate={formatDate}
            formatStartEndTime={formatStartEndTime}
            id={`follow-up-${opening.startTime}-${opening.providerId}`}
            key={`${opening.startTime}-${opening.providerId}`}
            startTimeISO={opening.startTime}
            title={recurringAppointmentType ?? 'Follow-up Session'}
          />
        ))}
      </ChakraVStack>
    </ChakraFlex>
  );
};

export default AppointmentList;
